import * as zod from 'zod';

import type { FeatureFlagKey } from '@sb/feathers-types';
import {
  BlendConfig,
  namesNoTooltip,
  StepTooltip,
  TCPOptionOrAuto,
  ZERO_BLEND_CONFIG,
} from '@sb/motion-planning';
import { MotionSpeed, SpeedLimitOption, Step } from '@sb/remote-control/types';
import { calculateSpeedProfileFromStepMotionSpeed } from '@sb/remote-control/util/calculateSpeedProfileFromStepMotionSpeed';
import { countStepKinds } from '@sb/remote-control/util/countStepKinds';
import { listAllSteps } from '@sb/remote-control/util/listAllSteps';
import { validateMotionSpeed } from '@sb/remote-control/util/validators';
import { ABSOLUTE_MAX_SPEED_PROFILE } from '@sb/routine-runner';
import type { StepKind, SpeedProfile } from '@sb/routine-runner';
import {
  getPermittedChildStepKindsShared,
  type MoveArmToV2PermittedChildArgs,
} from '@sb/routine-validation/step-rules/moveArmToV2';

/**
 * Types matching the routine runner schema for "MoveArmTo" steps.
 */
export namespace MoveArmToTargetStepV2 {
  export const name = 'Move arm';
  export const description =
    'Specify a location that the robot should go to by defining the joint or gripper positions';
  export const librarySection = Step.LibrarySection.Basic;
  export const librarySort = '1';
  export const featureFlag = 'ros2';
  export const argumentKind = 'MoveArmToV2';

  export const isDecorator = true;

  /**
   * @param featureFlags - The robot's feature flags.
   * @param args - This Move step's arguments, when the caller has a step in
   *   hand. Constant speed accepts Arc children on its own, so omitting them
   *   answers only for a step that has constant speed off.
   */
  export function getPermittedChildStepKinds(
    featureFlags: Partial<Record<FeatureFlagKey, boolean>> = {},
    args?: MoveArmToV2PermittedChildArgs,
  ): readonly StepKind[] {
    return getPermittedChildStepKindsShared(featureFlags, args);
  }

  export const alwaysShowDecoratorStepPlaceholder = true;

  /**
   * The substep kinds that contribute a motion target, and so count towards
   * "Move arm through N segments".
   *
   * Deliberately not the permitted-children list: what a step *contains* is not
   * what its flags currently *allow*. An arc already in the routine is a
   * segment whether or not `arcStepInMove` is on today, and the SetIO /
   * SetEnvironmentVariable children `allowSubstepsMoveStepV2` permits are not
   * segments at all. Mirrors the Weld step's `TARGET_POINTS_STEP_KIND`.
   */
  const MOTION_SUBSTEP_KINDS: StepKind[] = ['Waypoint', 'Arc'];

  export const Arguments = zod.object({
    argumentKind: zod.literal('MoveArmToV2'),
    motionSpeed: zod.preprocess(
      (val) => (val == null ? undefined : val),
      MotionSpeed.optional(),
    ),
    blendConfig: BlendConfig.optional(),
    speedLimitOption: SpeedLimitOption.exclude(['PARENT_DEFAULTS']).default(
      'NO_MOTION_LIMITS',
    ),
    moveDynamicBaseToReachPosition: zod.boolean().optional(),
    enforceConstantSpeed: zod.boolean().default(false),
    constantSpeedValue: zod.number().positive().optional(),
    constantSpeedAcceleration: zod.number().positive().optional(),
    constantSpeedTCPOption: TCPOptionOrAuto.optional(),
    // The tooltip this move's children inherit when they select "Parent
    // tooltip". Never `.default()` — absent on every step authored before the
    // field existed, and absent must affect nothing.
    stepTooltip: StepTooltip.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  // Constant-speed validation is split by the settings area it belongs to so
  // each error surfaces independently in the UI (and independently of the
  // step's other action-required, e.g. an empty decorator step's "Tap row
  // below to add step", which would otherwise mask them since
  // getStepActionRequired only surfaces the first error). The tooltip/limit
  // errors live under "Constant speed"; the blending error lives next to the
  // step's blending setting. The `validator` still throws the first of them so
  // playback is blocked on any.

  // A constant-speed move takes its tooltip from the Move step's own
  // `stepTooltip`; `constantSpeedTCPOption` is the field the removed
  // constant-speed picker wrote, and still governs the steps that predate it.
  //
  // Flag-gated because the flag decides which authoring surface exists: with it
  // on, the Move step's Tooltip row is always populated (an absent
  // `stepTooltip` reads there as the Active Tooltip), so there is nothing to
  // select. With it off there is no such row, and the legacy picker — which
  // shows a "Select a tooltip" placeholder rather than a resolved default — is
  // still the only way to choose one.
  const constantSpeedTooltipActionRequired = (
    args: Arguments,
    featureFlags: Partial<Record<FeatureFlagKey, boolean>>,
  ): Step.ActionRequired | undefined => {
    if (featureFlags.setActiveTooltip) {
      return undefined;
    }

    if (namesNoTooltip(args)) {
      return {
        kind: 'missingConfiguration',
        fieldId: 'constantSpeed',
        message: 'Select a tooltip for the constant speed motion.',
      };
    }

    return undefined;
  };

  // Constant speed can only hold a steady tooltip speed through corners if the
  // move blends between segments, so blending cannot be disabled/None while
  // constant speed is enabled. (Enabling constant speed defaults blending to
  // 25mm; this stops it being set back to 0.)
  const constantSpeedBlendingActionRequired = (
    args: Arguments,
  ): Step.ActionRequired | undefined => {
    if (!args.blendConfig?.radius) {
      return {
        kind: 'invalidConfiguration',
        fieldId: 'blending',
        message:
          'Constant speed requires blending. Set a blend radius greater than 0.',
      };
    }

    return undefined;
  };

  // Constant speed cannot exceed the step's default tooltip speed limit.
  const constantSpeedLimitActionRequired = (
    args: Arguments,
  ): Step.ActionRequired | undefined => {
    if (!args.constantSpeedValue) {
      return undefined;
    }

    // The step's effective tooltip speed limit for whichever speed-limit option
    // is selected — a custom value, a percent of robot max, or robot max (no
    // limit). Mirrors toRoutineRunner, which derives the step's speed profile
    // from motionSpeed over ABSOLUTE_MAX_SPEED_PROFILE, so the check fires for
    // the percent option (e.g. 1% of max), not just the fully-custom one.
    const { maxTooltipSpeed: tcpLimit } =
      calculateSpeedProfileFromStepMotionSpeed({
        motionSpeed: args.motionSpeed,
        baseSpeedProfile: ABSOLUTE_MAX_SPEED_PROFILE,
      });

    if (tcpLimit != null && args.constantSpeedValue > tcpLimit) {
      return {
        kind: 'invalidConfiguration',
        fieldId: 'constantSpeed',
        message:
          'Constant speed exceeds the default tooltip speed limit. Reduce the constant speed or increase the tooltip speed limit.',
      };
    }

    return undefined;
  };

  /**
   * The "Constant speed" settings error to surface: a missing tooltip, then a
   * constant speed exceeding the step's default tooltip speed limit. Returns
   * `undefined` when constant speed is off or that area is valid. Read directly
   * by the Advanced-section UI so it shows independently of the step's other
   * action-required (including the separate blending error below).
   */
  export function getConstantSpeedActionRequired(
    args: Arguments,
    featureFlags: Partial<Record<FeatureFlagKey, boolean>>,
  ): Step.ActionRequired | undefined {
    if (!args.enforceConstantSpeed) {
      return undefined;
    }

    return (
      constantSpeedTooltipActionRequired(args, featureFlags) ??
      constantSpeedLimitActionRequired(args)
    );
  }

  /**
   * The blending error to surface when constant speed is enabled without a
   * blend radius. Read directly by the blending setting's UI so it shows
   * independently of the "Constant speed" error above (e.g. a missing tooltip
   * and missing blending are shown at the same time, in their own areas).
   */
  export function getConstantSpeedBlendingActionRequired(
    args: Arguments,
  ): Step.ActionRequired | undefined {
    if (!args.enforceConstantSpeed) {
      return undefined;
    }

    return constantSpeedBlendingActionRequired(args);
  }

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const {
      motionSpeed,
      blendConfig,
      moveDynamicBaseToReachPosition,
      enforceConstantSpeed,
      constantSpeedValue,
      constantSpeedAcceleration,
      constantSpeedTCPOption,
      stepTooltip,
    } = args;

    validateMotionSpeed('Move Arm', motionSpeed);

    // The step's `motionSpeed` is the single source of truth for the joint
    // speed/acceleration limits, including during the constant-speed motion —
    // there is no separate constant-speed limits profile.
    const speedProfile: SpeedProfile = calculateSpeedProfileFromStepMotionSpeed(
      {
        motionSpeed,
        baseSpeedProfile,
      },
    );

    return {
      ...stepData,
      stepKind: 'MoveArmToV2',
      args: {
        speedProfile,
        blendConfig: blendConfig || ZERO_BLEND_CONFIG,
        moveDynamicBaseToReachPosition,
        enforceConstantSpeed,
        constantSpeedValue,
        constantSpeedAcceleration,
        constantSpeedTCPOption,
        stepTooltip,
      },
    };
  };

  /**
   * Step description for the move arm step is "Move arm through X points"
   */
  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration,
    routine,
    includeStepName,
  }) => {
    if (stepConfiguration?.stepKind !== argumentKind) return null;
    if (routine == null) return null;

    // Find the current step with all of its substeps
    const allSteps = listAllSteps(routine?.steps || []);
    const step = allSteps.find((s) => s.id === stepConfiguration.id);
    if (step === undefined) return null;

    const targetPoints = countStepKinds(step, MOTION_SUBSTEP_KINDS);

    const msg = [
      includeStepName ? 'Move arm' : false,
      `through ${targetPoints}`,
      `segment${targetPoints === 1 ? '' : 's'}`,
    ]
      .filter((s) => s != null && s !== false)
      .join(' ');

    return msg;
  };
}

MoveArmToTargetStepV2 satisfies Step.StepKindInfo;
